"use client";

import Link from "@/app/components/LocalizedLink";
import Button from "@/app/components/Button";
import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { localizedClipSrc } from "@/app/utils/localizedClip";

export type Step = {
  title: string;
  description: string;
  videoSrc?: string;
};

export type HowHeyFinnWorksProps = {
  title?: string;
  subtitle?: string;
  steps?: Step[];
  showCta?: boolean;
  showVideos?: boolean;
  className?: string;
};

// Video sources that don't need translation
const stepVideoSources = [
  "/videos/landing/SceneClip-JobCreation.mp4",
  "/videos/landing/SceneClip-Processing.mp4",
  "/videos/landing/SceneClip-ActivateDistribution.mp4",
  "/videos/landing/SceneClip-KanbanBoard.mp4",
];

// Hook to get translated default steps
function useDefaultSteps(): { title: string; subtitle: string; steps: Step[] } {
  const { t } = useTranslation();

  const title = t("howItWorks.componentTitle", "How heyFinn works");
  const subtitle = t(
    "howItWorks.componentSubtitle",
    "From job description to running social media campaign – in minutes instead of days."
  );

  const steps: Step[] = [
    {
      title: t("howItWorks.componentStep1Title", "Upload or paste job"),
      description: t(
        "howItWorks.componentStep1Description",
        "Copy your job description as text, PDF, or link into heyFinn. Optionally add your logo or team photo."
      ),
      videoSrc: stepVideoSources[0],
    },
    {
      title: t(
        "howItWorks.componentStep2Title",
        "AI creates ad & application page"
      ),
      description: t(
        "howItWorks.componentStep2Description",
        "heyFinn analyzes the details and creates a mobile-optimized ad, landing page, and 4-5 simple screening questions."
      ),
      videoSrc: stepVideoSources[1],
    },
    {
      title: t(
        "howItWorks.componentStep3Title",
        "Choose optional subscription – AI Social Boost"
      ),
      description: t(
        "howItWorks.componentStep3Description",
        "If you want, choose a heyFinn subscription with included ad budget. The AI handles targeting, variants, and distribution on Facebook & Instagram."
      ),
      videoSrc: stepVideoSources[2],
    },
    {
      title: t(
        "howItWorks.componentStep4Title",
        "Manage applications in dashboard"
      ),
      description: t(
        "howItWorks.componentStep4Description",
        "All applications arrive structured in the dashboard – including answers to screening questions and optional CV analysis."
      ),
      videoSrc: stepVideoSources[3],
    },
  ];

  return { title, subtitle, steps };
}

// Video component with lazy loading and autoplay on visibility
function StepVideo({ src, title }: { src: string; title: string }) {
  const { i18n } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  // Stays true after scrolling past, so the clip isn't dropped and fetched again
  const [hasBeenVisible, setHasBeenVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          setIsVisible(entry.isIntersecting);
          if (entry.isIntersecting) setHasBeenVisible(true);
        });
      },
      { threshold: 0.3 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!videoRef.current) return;

    if (isVisible && isLoaded) {
      videoRef.current.play().catch(() => {
        // Autoplay blocked - that's ok
      });
    } else {
      videoRef.current.pause();
    }
  }, [isVisible, isLoaded]);

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-video rounded-2xl overflow-hidden bg-gray-800 mb-4"
    >
      {!isLoaded && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-700 to-gray-800 animate-pulse flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
        </div>
      )}
      <video
        ref={videoRef}
        src={hasBeenVisible ? localizedClipSrc(src, i18n?.language) : undefined}
        className={`w-full h-full object-cover transition-opacity duration-300 ${
          isLoaded ? "opacity-100" : "opacity-0"
        }`}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        onLoadedData={() => setIsLoaded(true)}
        aria-label={title}
      />
    </div>
  );
}

export default function HowHeyFinnWorks({
  title: propTitle,
  subtitle: propSubtitle,
  steps: propSteps,
  showCta = false,
  showVideos = false,
  className,
}: HowHeyFinnWorksProps) {
  const { t } = useTranslation();
  const defaults = useDefaultSteps();

  const title = propTitle ?? defaults.title;
  const subtitle = propSubtitle ?? defaults.subtitle;
  const steps = propSteps ?? defaults.steps;

  return (
    <section
      aria-label={t("howItWorks.ariaLabel", "How heyFinn works")}
      className={`glass rounded-glass p-6 md:p-8 ${className ?? ""}`}
    >
      <div className="mb-6 md:mb-8">
        {title && (
          <h2 className="text-2xl md:text-3xl font-bold text-signal-ink">
            {title}
          </h2>
        )}
        {subtitle && <p className="mt-2 text-signal-muted">{subtitle}</p>}
      </div>
      <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 md:gap-6 list-none">
        {steps.slice(0, 5).map((step, index) => (
          <li
            key={`${step.title}-${index}`}
            className="glass-inset relative rounded-glassInner p-5 md:p-6"
          >
            <div
              aria-hidden
              className="absolute -top-3 -left-3 w-9 h-9 rounded-full bg-signal-accent text-white flex items-center justify-center font-semibold"
            >
              {index + 1}
            </div>
            {showVideos && step.videoSrc && (
              <StepVideo src={step.videoSrc} title={step.title} />
            )}
            <h3 className="text-lg font-semibold text-signal-ink">
              {step.title}
            </h3>
            <p className="mt-2 text-signal-muted">{step.description}</p>
          </li>
        ))}
      </ol>
      {showCta && (
        <div className="mt-6 md:mt-8 flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="text-signal-muted">
            {t("howItWorks.ctaQuestion", "Want to try the process?")}
          </p>
          <Link href="/register" className="inline-block">
            <Button className="btn-glass-primary border-0 !h-[40px] !px-4 text-sm !text-white">
              {t("howItWorks.ctaButton", "Generate job ad now")}
            </Button>
          </Link>
        </div>
      )}
    </section>
  );
}
